import './styles/graphic.scss';
import { csv } from 'd3-fetch';
import { scaleBand, scaleLinear } from 'd3-scale';
import { select } from 'd3-selection';
import { axisBottom, axisLeft } from 'd3-axis';
import { transition } from 'd3-transition';

// Extend selection prototype to include transition
select.prototype.transition = transition;

export default function (props, container, isMobile) {
  const {
    initialWidth,
    initialHeight,
    width,
    height,
    margins,
  } = props;

  // Common styling constants
  const commonStyles = {
    fontFamily: 'CNN, Arial, sans-serif',
    fontSize: 12,
    axisTextColor: '#B1B1B1',
    labelColor: '#6E6E6E',
  };

  // Responsive values
  const responsive = {
    legendY: isMobile ? -70 : -55,
    padding: isMobile ? 0.005 : 0.05,
    yPadding: isMobile ? 0.2 : 0.15,
    timeTicks: isMobile ? ['00:00', '06:00', '12:00', '18:00'] : ['00:00', '06:00', '12:00', '18:00', '23:00'],
    xAxisLabel: { y: isMobile ? initialHeight - 10 : initialHeight - 3 },
    yAxisLabel: { x: isMobile ? 15 : 18, y: isMobile ? 85 : 65 },
    yTickLabelOffset: isMobile ? -8 : -10, // Responsive offset for y-axis tick labels
    yTickLength: isMobile ? -5 : -6, // Responsive length for y-axis tick lines
  };

  // Helper functions for common patterns
  const createStyledText = (parent, className = '', x = 0, y = 0, text = '', options = {}) => parent.append('text')
    .attr('class', className)
    .attr('x', x)
    .attr('y', y)
    .attr('text-anchor', options.textAnchor || 'start')
    .attr('font-size', options.fontSize || commonStyles.fontSize)
    .attr('font-family', commonStyles.fontFamily)
    .attr('font-weight', options.fontWeight || 'normal')
    .style('fill', options.fill || commonStyles.axisTextColor)
    .text(text);

  const createStyledRect = (parent, className = '', x = 0, y = 0, width = 0, height = 0, options = {}) => parent.append('rect')
    .attr('class', className)
    .attr('x', x)
    .attr('y', y)
    .attr('width', width)
    .attr('height', height)
    .attr('fill', options.fill || 'none')
    .attr('stroke', options.stroke || 'none')
    .attr('stroke-width', options.strokeWidth || 0)
    .attr('opacity', options.opacity || 1)
    .attr('rx', options.rx || 0);

  const createBorders = (parent, x, y, width, height, className, strokeColor, strokeWidth = 1) => {
    // Left border
    createStyledRect(parent, className, x, y, strokeWidth, height, {
      stroke: strokeColor,
      strokeWidth,
    });
    // Right border
    createStyledRect(parent, className, x + width - strokeWidth, y, strokeWidth, height, {
      stroke: strokeColor,
      strokeWidth,
    });
  };

  // Helper function to get week start date
  const getWeekStartDate = (weekNumber) => {
    // January 6, 2025 is a Monday (week 1 starts)
    const week1Start = new Date(2025, 0, 6); // Month is 0-indexed
    const weekStart = new Date(week1Start);
    weekStart.setDate(week1Start.getDate() + (weekNumber - 1) * 7);

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];

    const month = monthNames[weekStart.getMonth()];
    const day = weekStart.getDate();

    return `${month} ${day}`;
  };

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', initialHeight);

  // Create main group for the heatmap
  const g = svg.append('g')
    .attr('transform', `translate(${margins.left},${margins.top})`);

  // Create tooltip outside the render function
  select('body').selectAll('.tooltip').remove();
  const tooltip = select('body').append('div')
    .attr('class', 'tooltip')
    .style('position', 'absolute')
    .style('background', 'rgba(0, 0, 0, 0.9)')
    .style('color', 'white')
    .style('padding', '10px')
    .style('border-radius', '6px')
    .style('font-size', `${commonStyles.fontSize}px`)
    .style('font-family', commonStyles.fontFamily)
    .style('pointer-events', 'none')
    .style('opacity', 0)
    .style('z-index', '1000')
    .style('box-shadow', '0 2px 8px rgba(0,0,0,0.3)');
  // Define renderHeatmap function first
  function renderHeatmap(data, tooltip, sunData = []) {
    // Clear existing content
    g.selectAll('*').remove();

    // Get unique values for scales
    const weeks = [...new Set(data.map(d => d.week))].sort((a, b) => a - b);
    let times = [...new Set(data.map(d => d.time))].sort();

    // Add sunrise/sunset times to the time domain if sun data is available
    if (sunData.length > 0) {
      const sunTimes = new Set();
      sunData.forEach((d) => {
        // Round sunrise/sunset to nearest hour for cleaner alignment
        const [sunriseHours, sunriseMinutes] = d.sunrise.split(':').map(Number);
        const [sunsetHours, sunsetMinutes] = d.sunset.split(':').map(Number);

        const sunriseHour = Math.round(sunriseHours + sunriseMinutes / 60);
        const sunsetHour = Math.round(sunsetHours + sunsetMinutes / 60);

        sunTimes.add(`${sunriseHour.toString().padStart(2, '0')}:00`);
        sunTimes.add(`${sunsetHour.toString().padStart(2, '0')}:00`);
      });
      // Merge and sort all times
      times = [...new Set([...times, ...sunTimes])].sort();
    }

    const values = data.map(d => d.value);
    const maxValue = Math.max(...values);

    const legendSettings = {
      width: 90,
      height: 15,
      x: 0,
      y: responsive.legendY,
      gap: 20,
    };

    // Create scales with responsive padding
    const xScale = scaleBand()
      .domain(times)
      .range([2, width]) // Add 2px padding from y-axis
      .padding(responsive.padding);

    const yScale = scaleBand()
      .domain(weeks)
      .range([0, height])
      .padding(responsive.yPadding);

    // Create a separate scale for daylight hours with no padding (continuous bars)
    const yScaleDaylight = scaleBand()
      .domain(weeks)
      .range([0, height])
      .padding(0); // No padding for continuous daylight bars

    const colorScale = scaleLinear()
      .domain([0, maxValue / 2, maxValue])
      .range(['#f0f8ff', '#5194C3', '#000033']);

    // Clear any existing daylight hours bars first
    g.selectAll('.daylight-hours').remove();

    // Add daylight hours background bars
    if (sunData.length > 0) {
      // Group sun data by week
      const sunDataByWeek = {};
      sunData.forEach((d) => {
        if (!sunDataByWeek[d.week]) {
          sunDataByWeek[d.week] = [];
        }
        sunDataByWeek[d.week].push(d);
      });

      // Create background rectangles for daylight hours
      const processedWeeks = new Set();

      // Only process weeks that exist in the heatmap data
      weeks.forEach((weekNum) => {
        // Skip if we've already processed this week
        if (processedWeeks.has(weekNum)) {
          return;
        }

        // Skip if this week doesn't have sun data
        if (!sunDataByWeek[weekNum]) {
          return;
        }

        processedWeeks.add(weekNum);

        const weekSunData = sunDataByWeek[weekNum];

        // Get average sunrise and sunset times for the week
        const avgSunrise = weekSunData.reduce((sum, d) => {
          const [hours, minutes] = d.sunrise.split(':').map(Number);
          return sum + hours + minutes / 60;
        }, 0) / weekSunData.length;

        const avgSunset = weekSunData.reduce((sum, d) => {
          const [hours, minutes] = d.sunset.split(':').map(Number);
          return sum + hours + minutes / 60;
        }, 0) / weekSunData.length;

        // Round to nearest hour, but be conservative with sunset
        const sunriseHour = Math.round(avgSunrise);
        const sunsetHour = Math.floor(avgSunset); // Round down to be conservative

        // Convert to time format for x-scale (hourly)
        const sunriseTime = `${sunriseHour.toString().padStart(2, '0')}:00`;
        const sunsetTime = `${sunsetHour.toString().padStart(2, '0')}:00`;

        const sunriseX = xScale(sunriseTime);
        const sunsetX = xScale(sunsetTime);
        const width = sunsetX - sunriseX;

        // Only create rectangle if width is valid
        if (!isNaN(width) && width > 0) {
          // Main daylight rectangle
          g.append('rect')
            .attr('x', sunriseX)
            .attr('y', yScaleDaylight(weekNum))
            .attr('width', width)
            .attr('height', yScaleDaylight.bandwidth()) // Full height with no gaps
            .attr('fill', '#F8C153') // New yellow color
            .attr('opacity', 0.2) // 20% opacity
            .attr('class', 'daylight-hours');
        }
      });
    }
    // Create heatmap rectangles
    const rects = g.selectAll('.heatmap-rect')
      .data(data, d => `${d.week}-${d.time}`) // Use key function for proper data binding
      .enter()
      .append('rect')
      .attr('class', 'heatmap-rect')
      .attr('x', d => xScale(d.time))
      .attr('y', d => yScale(d.week))
      .attr('width', xScale.bandwidth())
      .attr('height', yScale.bandwidth())
      .attr('fill', d => colorScale(d.value))
      .attr('stroke', '#fff')
      .attr('stroke-width', 0.5);

    // Highlight weeks 35-37 with one black border rectangle
    const highlightWeeks = [34, 35, 36, 37];
    const existingWeeks = highlightWeeks.filter(week => weeks.includes(week));

    if (existingWeeks.length > 0) {
      const startWeek = Math.min(...existingWeeks);
      const endWeek = Math.max(...existingWeeks);
      const startY = yScale(startWeek);
      const endY = yScale(endWeek) + yScale.bandwidth();
      const totalHeight = endY - startY;

      g.append('rect')
        .attr('x', 2) // Match x-scale start position
        .attr('y', startY)
        .attr('width', width - 2) // Adjust width to account for padding
        .attr('height', totalHeight)
        .attr('fill', 'none')
        .attr('stroke', '#000000')
        .attr('stroke-width', 1)
        .attr('class', 'week-highlight');
    }

    // Add legend above daylight hours - left aligned
    const legend = g.append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${legendSettings.x}, ${legendSettings.y})`);

    // Create gradient definition
    const defs = svg.append('defs');
    const gradient = defs.append('linearGradient')
      .attr('id', 'legendGradient')
      .attr('x1', '0%')
      .attr('x2', '100%')
      .attr('y1', '0%')
      .attr('y2', '0%');

    // Add gradient stops
    gradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#f0f8ff');

    gradient.append('stop')
      .attr('offset', '50%')
      .attr('stop-color', '#5194C3');

    gradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#000033');

    // Add gradient rectangle
    legend.append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', legendSettings.width)
      .attr('height', legendSettings.height)
      .attr('fill', 'url(#legendGradient)');

    // Legend labels using helper functions
    createStyledText(legend, '', 0, legendSettings.height + 15, '0');
    createStyledText(legend, '', legendSettings.width, legendSettings.height + 15, maxValue, {
      textAnchor: 'end',
    });
    createStyledText(legend, '', legendSettings.width / 2, -5, 'Air raid alerts', {
      textAnchor: 'middle',
      fontWeight: '700',
      fill: commonStyles.labelColor,
    });
    // Add daylight hours label in the middle of the heatmap
    const daylightStart = xScale('07:00');
    const daylightEnd = xScale('19:00');

    if (daylightStart !== undefined && daylightEnd !== undefined) {
      // Both mobile and desktop: position next to the "Air raid alerts" legend with gap
      const daylightX = legendSettings.width + legendSettings.gap; // Position after first legend + gap

      // Add yellow background rectangle (same size as legend)
      createStyledRect(g, '', daylightX, legendSettings.y, legendSettings.width, legendSettings.height, {
        fill: '#F8C153',
        opacity: 0.2,
        rx: 3,
      });

      // Add borders
      createBorders(g, daylightX, legendSettings.y, legendSettings.width, legendSettings.height, 'daylight-legend-border', '#F8C153');

      // Add "Daylight hours" text above the rectangle (like legend)
      createStyledText(g, 'daylight-label', daylightX + legendSettings.width / 2, legendSettings.y - 5, 'Daylight hours', {
        textAnchor: 'middle',
        fontWeight: '700',
        fill: commonStyles.labelColor,
      });
    }

    // Create custom x-axis instead of using D3 axis
    const { timeTicks } = responsive;

    // Add custom axis line
    g.append('line')
      .attr('class', 'x-axis-line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', height)
      .attr('y2', height)
      .style('stroke', '#B1B1B1')
      .style('stroke-width', 1);

    // Add custom tick lines only for labeled times
    timeTicks.forEach((time) => {
      const x = xScale(time) + xScale.bandwidth() / 2;

      // Add tick line
      g.append('line')
        .attr('class', 'x-tick')
        .attr('x1', x)
        .attr('x2', x)
        .attr('y1', height)
        .attr('y2', height + 6)
        .style('stroke', '#B1B1B1')
        .style('stroke-width', 1);

      // Add tick label
      const timeLabels = {
        '00:00': 'Midnight',
        '06:00': '6 a.m.',
        '12:00': '12 p.m.',
        '18:00': '6 p.m.',
        '23:00': '11 p.m.',
      };

      createStyledText(g, 'x-tick-label', x, height + 20, timeLabels[time] || time, {
        textAnchor: 'middle',
      });
    });

    // Add custom y-axis line (left edge)
    g.append('line')
      .attr('class', 'y-axis-line')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', height)
      .style('stroke', commonStyles.axisTextColor)
      .style('stroke-width', 1);

    // Add y-axis labels every 4 weeks (4, 8, 12, etc.) plus week 1
    const weekTicks = weeks.filter(d => d % 4 === 0 || d === 1); // Show weeks 1, 4, 8, 12, etc.

    weekTicks.forEach((week) => {
      const y = yScale(week) + yScale.bandwidth() / 2; // Center of the rectangle

      // Add tick line
      g.append('line')
        .attr('class', 'y-tick')
        .attr('x1', responsive.yTickLength)
        .attr('x2', 0)
        .attr('y1', y)
        .attr('y2', y)
        .style('stroke', commonStyles.axisTextColor)
        .style('stroke-width', 1);

      // Add tick label
      createStyledText(g, 'y-tick-label', responsive.yTickLabelOffset, y + 3, week, {
        textAnchor: 'end',
      });
    });

    // Add axis labels after scales are defined
    // Center the x-axis label in the middle of the grid
    const gridCenterX = margins.left + (width / 2);
    createStyledText(svg, 'x-axis-label', gridCenterX, responsive.xAxisLabel.y, 'Time of day', {
      textAnchor: 'middle',
      fill: commonStyles.labelColor,
    });

    // Position "Week" label at the top of the y-axis
    createStyledText(svg, 'y-axis-label', responsive.yAxisLabel.x, responsive.yAxisLabel.y, 'Week', {
      textAnchor: 'middle',
      fill: commonStyles.labelColor,
    });
    // Add event handlers to the rectangles
    rects.each(function (d) {
      const rect = select(this);
      rect
        .on('mouseover', (event) => {
          // Get rectangle position relative to the viewport
          const rect = this.getBoundingClientRect();
          const tooltipWidth = 120;
          const tooltipHeight = 60;

          // Calculate position to appear next to the rectangle
          let left = rect.right + 10; // 10px to the right of the rectangle
          let top = rect.top + (rect.height / 2) - (tooltipHeight / 2); // Centered vertically

          // Adjust if tooltip would go off screen
          if (left + tooltipWidth > window.innerWidth) {
            left = rect.left - tooltipWidth - 10; // Show to the left instead
          }
          if (top < 0) {
            top = 10; // Keep it within viewport
          }
          if (top + tooltipHeight > window.innerHeight) {
            top = window.innerHeight - tooltipHeight - 10;
          }

          // Check if this rectangle is during daylight hours for this specific week
          let isDuringDaylight = false;
          if (sunData.length > 0) {
            // Find sun data for this week
            const weekSunData = sunData.filter(sun => sun.week === d.week);
            if (weekSunData.length > 0) {
              // Calculate average sunrise and sunset times for the week
              const avgSunrise = weekSunData.reduce((sum, sun) => {
                const [hours, minutes] = sun.sunrise.split(':').map(Number);
                return sum + hours + minutes / 60;
              }, 0) / weekSunData.length;

              const avgSunset = weekSunData.reduce((sum, sun) => {
                const [hours, minutes] = sun.sunset.split(':').map(Number);
                return sum + hours + minutes / 60;
              }, 0) / weekSunData.length;

              // Round to nearest hour, but be conservative with sunset
              const sunriseHour = Math.round(avgSunrise);
              const sunsetHour = Math.floor(avgSunset); // Round down to be conservative

              // Convert to time format for x-scale (hourly)
              const sunriseTime = `${sunriseHour.toString().padStart(2, '0')}:00`;
              const sunsetTime = `${sunsetHour.toString().padStart(2, '0')}:00`;

              // Check if current time is within daylight hours (strict comparison for sunset)
              isDuringDaylight = d.time >= sunriseTime && d.time < sunsetTime;
            }
          }

          // Format time like x-axis (e.g., "1 a.m.", "2 p.m.")
          const formatTime = (timeStr) => {
            const [hours, minutes] = timeStr.split(':').map(Number);
            const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
            const ampm = hours < 12 ? 'a.m.' : 'p.m.';
            return `${hour12} ${ampm}`;
          };

          tooltip
            .style('opacity', 0.95)
            .html(`
              <div style="font-weight: bold; margin-bottom: 4px;">Week ${d.week} (${getWeekStartDate(d.week)})</div>
              <div style="margin-bottom: 8px;">${formatTime(d.time)}</div>
              <div>Air raid alerts: <strong>${d.value}</strong></div>
              ${isDuringDaylight ? '<div style="color: #F8C153; font-weight: bold;">During daylight</div>' : ''}
            `)
            .style('left', `${left}px`)
            .style('top', `${top}px`);
        })
        .on('mouseout', () => {
          tooltip.style('opacity', 0);
        });
    });

    // Add daylight borders on top of heatmap rectangles
    if (sunData.length > 0) {
      // Group sun data by week
      const sunDataByWeek = {};
      sunData.forEach((d) => {
        if (!sunDataByWeek[d.week]) {
          sunDataByWeek[d.week] = [];
        }
        sunDataByWeek[d.week].push(d);
      });

      // Create border rectangles for daylight hours
      const processedWeeks = new Set();

      // Only process weeks that exist in the heatmap data
      weeks.forEach((weekNum) => {
        // Skip if we've already processed this week
        if (processedWeeks.has(weekNum)) {
          return;
        }

        // Skip if this week doesn't have sun data
        if (!sunDataByWeek[weekNum]) {
          return;
        }

        processedWeeks.add(weekNum);

        const weekSunData = sunDataByWeek[weekNum];

        // Get average sunrise and sunset times for the week
        const avgSunrise = weekSunData.reduce((sum, d) => {
          const [hours, minutes] = d.sunrise.split(':').map(Number);
          return sum + hours + minutes / 60;
        }, 0) / weekSunData.length;

        const avgSunset = weekSunData.reduce((sum, d) => {
          const [hours, minutes] = d.sunset.split(':').map(Number);
          return sum + hours + minutes / 60;
        }, 0) / weekSunData.length;

        // Round to nearest hour, but be conservative with sunset
        const sunriseHour = Math.round(avgSunrise);
        const sunsetHour = Math.floor(avgSunset); // Round down to be conservative

        // Convert to time format for x-scale (hourly)
        const sunriseTime = `${sunriseHour.toString().padStart(2, '0')}:00`;
        const sunsetTime = `${sunsetHour.toString().padStart(2, '0')}:00`;

        const sunriseX = xScale(sunriseTime);
        const sunsetX = xScale(sunsetTime);
        const width = sunsetX - sunriseX;

        // Only create borders if width is valid
        if (!isNaN(width) && width > 0) {
          // Add borders
          createBorders(g, sunriseX, yScaleDaylight(weekNum), sunsetX - sunriseX, yScaleDaylight.bandwidth(), 'daylight-border', '#F8C153');
        }
      });
    }
  }

  // Load both datasets
  let parsedData = [];
  let sunData = [];

  Promise.all([
    csv('./ukr-raids-week-by-week-long.csv'),
    csv('./kyiv_sunrise_sunset_2025.csv'),
  ]).then(([raidData, sunDataRaw]) => {
    parsedData = raidData.map(d => ({
      week: +d.week,
      time: d.time,
      value: +d.value,
    }));

    // Parse sun data and map to weeks (matching heatmap week numbering)
    sunData = sunDataRaw.map((d) => {
      const date = new Date(d.date);
      const startOfYear = new Date('2025-01-01');
      // Calculate week number to match heatmap (week 1 = first week of 2025)
      const daysDiff = Math.floor((date - startOfYear) / (24 * 60 * 60 * 1000));
      const week = Math.floor(daysDiff / 7) + 1;
      return {
        week,
        sunrise: d.sunrise,
        sunset: d.sunset,
        date,
      };
    });

    // Render with both datasets
    renderHeatmap(parsedData, tooltip, sunData);
  }).catch((error) => {
    console.error('Data loading failed:', error);
  });
}
